import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Space, Input, Button, TextArea, Toast } from 'antd-mobile'
import {
  getAccessToken,
  getNewAccessToken,
  getRefreshToken,
  refreshTokenWithLock,
  removeAccessToken,
  removeRefreshToken,
  storeAccessToken,
  storeRefreshToken,
} from '@/utils/tokenUtils'
import { useMutation } from '@tanstack/react-query'
import Sentry from '@/sentry'
import { copyToClipboard } from '@/utils/clipboard'

// 提取样式常量，避免内联样式冗余
const SPACE_STYLE: React.CSSProperties = {
  padding: '20px',
  position: 'absolute' as const,
  left: 0,
  right: 0,
  marginInline: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}

const INPUT_STYLE: React.CSSProperties = {
  border: '1px solid #ccc',
  borderRadius: '5px',
  padding: '10px',
  width: '100%', // 统一宽度，避免响应式问题
}

const TEXTAREA_STYLE: React.CSSProperties = {
  ...INPUT_STYLE, // 复用输入框基础样式
  height: '100px',
  marginTop: '10px',
}

export const Route = createFileRoute('/test/token-login')({
  component: RouteComponent,
})
async function copy(text: string) {
  const ok = await copyToClipboard(text)
  Toast.show({ content: ok ? '复制成功' : '复制失败，请手动复制', duration: 1500 })
}
/**
 * 手动登录页面组件
 * 用于输入 refresh_token 进行登录验证
 */
function RouteComponent() {
  const navigate = useNavigate()
  // 状态声明集中管理
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [accessToken, setAccessToken] = useState(getAccessToken() || '')
  const { mutate: refreshToken, isPending: refreshing } = useMutation({
    mutationFn: refreshTokenWithLock,
    onSuccess: (data) => {
      storeAccessToken(data)
      setAccessToken(data)
      Toast.show('刷新成功')
    },
    onMutate: () => {
      Toast.show('刷新中')
    },
    onError: (error) => {
      Sentry.captureException(error)
      Toast.show('刷新失败')
    },
  })
  // 复制逻辑封装
  const handleCopyAccessToken = () => {
    const token = getAccessToken()
    if (!token) {
      Toast.show({ content: '没有可复制的 refresh_token', duration: 1500 })
      return
    }
    copy(token)
  }
  const handleCopyRefreshToken = () => {
    const token = getRefreshToken()
    if (!token) {
      Toast.show({ content: '没有可复制的 refresh_token', duration: 1500 })
      return
    }
    copy(token)
  }

  // 提交逻辑封装
  const handleSubmit = async () => {
    if (!input.trim()) {
      // 使用 trim() 避免空格输入
      Toast.show({ content: '请输入有效的 refresh_token', duration: 1500 })
      return
    }
    setLoading(true)
    // 尝试刷新token
    try {
      const access_token = await getNewAccessToken(input)
      storeAccessToken(access_token)
      storeRefreshToken(input)
      navigate({ to: '/courses', replace: true })
    } catch (error: any) {
      Toast.show({ content: '刷新 token 失败，请重试', duration: 1500 })
      throw new Error('刷新 token 失败:' + error?.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Space direction="vertical" style={SPACE_STYLE}>
      <Input
        value={input}
        onChange={setInput}
        placeholder="请输入 refresh_token"
        style={INPUT_STYLE}
      />

      <Button
        color="primary"
        onClick={handleSubmit}
        style={{ width: '100%' }}
        loading={loading}
      >
        登录
      </Button>
      <TextArea
        value={accessToken}
        readOnly
        placeholder="当前 access_token 将显示在这里"
        style={TEXTAREA_STYLE}
      />
      <Button
        onClick={handleCopyAccessToken}
        color="primary"
        style={{ marginTop: '10px', width: '100%' }}
      >
        复制 access_token
      </Button>
      <Button
        onClick={() => refreshToken()}
        color="primary"
        style={{ marginTop: '10px', width: '100%' }}
        loading={refreshing}
      >
        刷新 access_token
      </Button>
      <TextArea
        value={getRefreshToken() || ''}
        readOnly
        placeholder="当前 refresh_token 将显示在这里"
        style={TEXTAREA_STYLE}
      />
      <Button
        onClick={handleCopyRefreshToken}
        color="primary"
        style={{ marginTop: '10px', width: '100%' }}
      >
        复制 refresh_token
      </Button>
      <Button
        onClick={() => navigate({ to: '/login', replace: true })}
        color="success"
        style={{ marginTop: '10px', width: '100%' }}
      >
        返回微信授权登录
      </Button>
      <Button
        onClick={() => {
          removeAccessToken()
          removeRefreshToken()
          setAccessToken('')
          setInput('')
        }}
        color="danger"
        style={{ marginTop: '10px', width: '100%' }}
      >
        清理所有token
      </Button>
    </Space>
  )
}
